// Closing-gate Bypass initiation affordance.
//
// Renders on the NEW-MODEL Escrow Setup view (no legacy row + no manual
// completion path), self-gated on the ADMIN-only BypassClosingGate permission.
// Clicking opens the reason modal; on confirm it POSTs the bypass and the hook
// invalidates the escrow query so the section re-renders to the bypassed state.
// Disabled, with the reason shown, once the deal's escrow was released (owner
// ruling, 2026-10-09) or while the server would refuse it for real escrow
// activity (owner ruling, 2026-10-26); see escrowBypassAvailability.ts.

import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { DEAL_FLOW_PERMISSIONS } from "@/lib/dealFlowPermissions";
import { useBypassEscrowGate } from "@/hooks/dealFlow/useDealGateBypass";
import { useDealEscrowStatus } from "@/hooks/dealFlow/useDealFlow";
import { BypassConfirmModal } from "./BypassConfirmModal";
import { escrowBypassBlockedReason } from "./escrowBypassAvailability";

interface BypassEscrowGateButtonProps {
  dealId: string;
}

export function BypassEscrowGateButton({ dealId }: BypassEscrowGateButtonProps) {
  const { hasPermission } = useAuth();
  const canBypass = hasPermission(DEAL_FLOW_PERMISSIONS.BypassClosingGate);
  const bypass = useBypassEscrowGate(dealId);
  const escrow = useDealEscrowStatus(canBypass ? dealId : undefined);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  if (!canBypass) {
    return null;
  }

  const blocked = escrowBypassBlockedReason(escrow.data);

  const onConfirm = async (reason: string) => {
    setPending(true);
    try {
      await bypass.mutateAsync(reason);
      toast.success("Escrow gate bypassed for testing.");
      setOpen(false);
    } catch (err) {
      // 422 (real escrow activity) / 404 / 400 messages surface verbatim.
      toast.error(extractErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="border-t border-border-light pt-space-3 mt-space-3">
      <div className="flex items-center justify-between gap-space-3 flex-wrap">
        <p className="text-caption text-gray-mid m-0 max-w-reading">
          <span className="font-semibold text-status-warning">Admin / QA:</span>{" "}
          force-complete this escrow gate so a test deal can advance to loan
          boarding without a real, funded close.
        </p>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setOpen(true)}
          disabled={escrow.isLoading || blocked !== null}
          title={blocked ?? undefined}
          data-testid="bypass-escrow-button"
        >
          Bypass escrow (test)
        </Button>
      </div>
      {blocked && (
        <p className="text-caption text-gray-mid mt-space-2 mb-0" data-testid="bypass-escrow-blocked">
          {blocked}
        </p>
      )}
      <BypassConfirmModal
        open={open}
        onClose={() => (pending ? undefined : setOpen(false))}
        onConfirm={onConfirm}
        isPending={pending}
      />
    </div>
  );
}
